import { inject, signal } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'

import { takeUntil } from 'rxjs'

import { ApiErrors } from '@ti/contracts'
import { PasswordValidator } from '@ti/core/auth/utils'
import { Memoize } from '@ti/core/common/decorators'
import {
  checkExistApiError,
  getApiErrorKey,
  getMessageFromException,
  injectDestroy$,
} from '@ti/core/common/utils'
import { LanguageService } from '@ti/core/ui-kit/i18n'

export type NewPasswordType = 'create' | 'change' | 'reset'

export abstract class NewPasswordViewModel {
  protected readonly languageService = inject(LanguageService)
  protected readonly destroy$ = injectDestroy$()

  public readonly isLoading = signal(false)
  public readonly apiErrorMessage = signal('')
  public readonly newPasswordSaved = signal(false)

  public readonly mode = signal<NewPasswordType>('reset')
  public readonly secret = signal<string | undefined>(undefined)

  @Memoize()
  public get form() {
    const form = new FormGroup<{
      current?: FormControl<string | null>
      password: FormControl<string | null>
      repeatPassword: FormControl<string | null>
    }>({
      password: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(8),
        PasswordValidator,
      ]),
      repeatPassword: new FormControl<string>('', [Validators.required]),
    })

    form.controls.password.addValidators([
      (control) => {
        return control.value === this.form.controls.repeatPassword.value
          ? null
          : { 'passwords-are-not-equal': true }
      },
    ])
    form.controls.repeatPassword.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      form.controls.password.updateValueAndValidity()
    })

    if (this.mode() === 'change') {
      form.addControl('current', new FormControl<string>('', [Validators.required]))

      form.controls.password.addValidators([
        (control) => {
          if (!control.value || !this.form.controls.current) {
            return null
          }
          return control.value !== this.form.controls.current.value
            ? null
            : { 'new-password-equal-previous': true }
        },
      ])

      form.controls.current?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        form.controls.password.updateValueAndValidity()
      })
    }

    return form
  }

  public initialize(mode: NewPasswordType, secret?: string) {
    this.mode.set(mode)
    this.secret.set(secret)
  }

  public async submitForm() {
    if (this.isLoading()) {
      return
    }

    if (!this.form.valid) {
      return this.form.markAllAsTouched()
    }

    const secret = this.secret()
    if (secret) {
      await this.savePasswordForAnonymous(this.form.controls.password.value as string, secret)
    } else {
      await this.savePasswordForUser(
        this.form.controls.current?.value as string,
        this.form.controls.password.value as string,
      )
    }
  }

  // Change password for authenticated user
  public async savePasswordForUser(currentPassword: string, newPassword: string) {
    try {
      this.isLoading.set(true)
      await this.changePassword(currentPassword, newPassword)
      this.newPasswordSaved.set(true)
    } catch (error) {
      console.warn(`Cannot change password`, error)
      const message = getMessageFromException(error)
      this.apiErrorMessage.set(
        await this.languageService.translate(
          checkExistApiError(message) ? getApiErrorKey(message) : getApiErrorKey(ApiErrors.unknown),
        ),
      )
    } finally {
      this.isLoading.set(false)
    }
  }

  // Used for invite and reset-password flow
  public async savePasswordForAnonymous(password: string, secret: string) {
    try {
      this.isLoading.set(true)
      await this.confirmPasswordReset(password, secret)
      this.newPasswordSaved.set(true)
    } catch (error) {
      console.warn(`Cannot save password`, error)
      const message = getMessageFromException(error)
      this.apiErrorMessage.set(
        await this.languageService.translate(
          checkExistApiError(message) ? getApiErrorKey(message) : getApiErrorKey(ApiErrors.unknown),
        ),
      )
    } finally {
      this.isLoading.set(false)
    }
  }

  protected abstract changePassword(currentPassword: string, newPassword: string): Promise<void>

  protected abstract confirmPasswordReset(password: string, secret: string): Promise<void>
}
